// src/pages/StudentAcademicPage.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Stack, Paper, Tabs, Tab, Button, Chip, Breadcrumbs, Link
} from '@mui/material';
import { motion } from 'framer-motion';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import QuizIcon from '@mui/icons-material/Quiz';
import SportsIcon from '@mui/icons-material/Sports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BookIcon from '@mui/icons-material/Book';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';

// Academic Tab Components
import StudentAttendanceTab from '../components/academicTabs/StudentAttendanceTab';
import StudentTestsTab from '../components/academicTabs/StudentTestsTab';
import StudentActivitiesTab from '../components/academicTabs/StudentActivitiesTab';
import StudentAchievementsTab from '../components/academicTabs/StudentAchievementsTab';
import StudentAssignmentsTab from '../components/academicTabs/StudentAssignmentsTab';
import StudentSubjectsTab from '../components/academicTabs/StudentSubjectsTab';
import StudentReportCardTab from '../components/academicTabs/StudentReportCardTab';
import StudentTranscriptTab from '../components/academicTabs/StudentTranscriptTab';

// Redux actions (you'll need to create these)
import { fetchStudentById } from '../slices/studentsSlice';

const MotionPaper = motion(Paper);

// Tab Panel Component
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`academic-tabpanel-${index}`}
      aria-labelledby={`academic-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const StudentAcademicPage = () => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);

  // Redux state
  const { selectedStudent, selectedStudentStatus, selectedStudentError } = useSelector(
    (state) => state.students
  );

  // Fetch student data
  useEffect(() => {
    if (studentId) {
      dispatch(fetchStudentById(studentId));
    }
  }, [studentId, dispatch]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const getStudentFullName = () => {
    if (!selectedStudent) return 'غير محدد';
    return `${selectedStudent.first_name || ''} ${selectedStudent.second_name || ''}`.trim();
  };

  const getClassInfo = () => {
    // First check if school_class_name is available (from serializer)
    if (selectedStudent?.school_class_name) {
      return selectedStudent.school_class_name;
    }
    // Fallback: check if school_class is an object with name
    if (selectedStudent?.school_class && typeof selectedStudent.school_class === 'object') {
      return selectedStudent.school_class.name || 'غير محدد';
    }
    // If school_class is just an ID, return 'غير محدد'
    if (selectedStudent?.school_class) {
      return 'غير محدد';
    }
    return 'غير محدد';
  };

  // Loading state
  if (selectedStudentStatus === 'loading') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ direction: 'rtl', bgcolor: '#F8F9FF' }}
      >
        <Typography
          align="center"
          sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', fontWeight: 700, color: '#6B7280' }}
        >
          جاري تحميل بيانات الطالب...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (selectedStudentStatus === 'failed') {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ direction: 'rtl', bgcolor: '#F8F9FF' }}
      >
        <Typography
          align="center"
          sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', fontWeight: 700, color: '#DC2626' }}
        >
          {selectedStudentError || 'حدث خطأ أثناء تحميل بيانات الطالب'}
        </Typography>
      </Box>
    );
  }

  return (
    // 🎨 Design System Applied
    <Box
      sx={{
        direction: 'rtl',
        fontFamily: 'Cairo, Tajawal, sans-serif',
        p: { xs: 2, md: 3 },
        bgcolor: '#F8F9FF',
        minHeight: '100vh',
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>

        {/* Breadcrumbs */}
        <Breadcrumbs
          aria-label="breadcrumb"
          sx={{
            mb: 2.5,
            fontFamily: 'Cairo, Tajawal, sans-serif',
            '& .MuiBreadcrumbs-separator': { color: '#9CA3AF' },
          }}
        >
          <Link
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#6B7280',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              fontWeight: 600,
              '&:hover': { color: '#4338CA' },
            }}
            color="inherit"
            onClick={() => navigate('/')}
          >
            <HomeIcon sx={{ ml: 0.5 }} fontSize="inherit" />
            الرئيسية
          </Link>
          <Link
            underline="hover"
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: '#6B7280',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              fontWeight: 600,
              '&:hover': { color: '#4338CA' },
            }}
            color="inherit"
            onClick={handleGoBack}
          >
            <SchoolIcon sx={{ ml: 0.5 }} fontSize="inherit" />
            الصف {getClassInfo()}
          </Link>
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              color: '#1E1B4B',
              fontFamily: 'Cairo, Tajawal, sans-serif',
              fontWeight: 700,
            }}
          >
            <PersonIcon sx={{ ml: 0.5 }} fontSize="inherit" />
            الملف الأكاديمي
          </Typography>
        </Breadcrumbs>

        {/* Student Header */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          sx={{
            p: { xs: 2.5, sm: 3 },
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
            color: '#fff',
          }}
        >
          <Stack useFlexGap direction="row" alignItems="center" spacing={2} sx={{ flexWrap: 'wrap', gap: 2 }}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                borderRadius: 3,
                width: { xs: 60, md: 76 },
                height: { xs: 60, md: 76 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PersonIcon sx={{ color: '#fff', fontSize: { xs: 32, md: 40 } }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                color="#fff"
                gutterBottom
                sx={{ fontFamily: 'Cairo, Tajawal, sans-serif', fontSize: { xs: '1.35rem', md: '1.6rem' } }}
              >
                {getStudentFullName()}
              </Typography>

              <Stack useFlexGap direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`الصف: ${getClassInfo()}`}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                />
                <Chip
                  label={`رقم الطالب: ${selectedStudent?.student_id || 'غير محدد'}`}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.18)',
                    color: '#fff',
                    fontWeight: 700,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}
                />
                {selectedStudent?.is_bus_joined && (
                  <Chip
                    label="مشترك في النقل"
                    sx={{
                      backgroundColor: 'rgba(22,163,74,0.85)',
                      color: '#fff',
                      fontWeight: 700,
                      fontFamily: 'Cairo, Tajawal, sans-serif',
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleGoBack}
              sx={{
                borderColor: 'rgba(255,255,255,0.6)',
                color: '#fff',
                borderRadius: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                px: 2.5,
                fontFamily: 'Cairo, Tajawal, sans-serif',
                '&:hover': {
                  borderColor: '#fff',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                },
              }}
            >
              العودة
            </Button>
          </Stack>
        </MotionPaper>

        {/* Academic Tabs */}
        <MotionPaper
          component={motion.div}
          elevation={0}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.1, ease: 'easeOut' }}
          sx={{
            borderRadius: 3,
            background: '#fff',
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
            border: '1px solid #E5E7EB',
            overflow: 'hidden',
          }}
        >
          <Box sx={{ borderBottom: '1px solid #E5E7EB', bgcolor: '#F8FAFF' }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 64,
                  fontSize: '0.9rem',
                  fontWeight: 700,
                  textTransform: 'none',
                  color: '#6B7280',
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  '&.Mui-selected': {
                    color: '#4338CA',
                    backgroundColor: '#EEF2FF',
                  },
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                  backgroundColor: '#4338CA',
                },
              }}
            >
              <Tab
                icon={<EventAvailableIcon />}
                label="الحضور والغياب"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<QuizIcon />}
                label="الاختبارات والدرجات"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<AssignmentIcon />}
                label="الواجبات المنزلية"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<BookIcon />}
                label="المواد الدراسية"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<SportsIcon />}
                label="الأنشطة"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<EmojiEventsIcon />}
                label="الإنجازات والجوائز"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<DescriptionIcon />}
                label="كشف الدرجات"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
              <Tab
                icon={<DescriptionIcon />}
                label="السجل الأكاديمي"
                iconPosition="start"
                sx={{ gap: 1 }}
              />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            <TabPanel value={tabValue} index={0}>
              <StudentAttendanceTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <StudentTestsTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <StudentAssignmentsTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={3}>
              <StudentSubjectsTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={4}>
              <StudentActivitiesTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={5}>
              <StudentAchievementsTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={6}>
              <StudentReportCardTab student={selectedStudent} />
            </TabPanel>

            <TabPanel value={tabValue} index={7}>
              <StudentTranscriptTab student={selectedStudent} />
            </TabPanel>
          </Box>
        </MotionPaper>
      </Box>
    </Box>
  );
};

export default StudentAcademicPage;